import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
  type FC,
} from 'react';
import {
  useNebulaIntegrationsData,
  ParsedNebulaIntegration,
} from '../hooks/useNebulaIntegrationsData';
import {
  useTdmsServicesData,
  PlatformBooking,
} from '../hooks/useTdmsServicesData';

interface RepositoryOnboardingContextValue {
  repositoryValue: string;
  integrationData: ParsedNebulaIntegration | null;
  integrationLoading: boolean;
  integrationError?: Error;
  tdmsServices: PlatformBooking[] | null;
  tdmsLoading: boolean;
  tdmsError?: Error;
}

const RepositoryOnboardingContext = createContext<
  RepositoryOnboardingContextValue | undefined
>(undefined);

interface RepositoryOnboardingProviderProps {
  children: ReactNode;
  repositoryValue: string;
}

export const RepositoryOnboardingProvider: FC<
  RepositoryOnboardingProviderProps
> = ({ children, repositoryValue }) => {
  // Single source of truth for TechInsights data
  const {
    data: integrationData,
    loading: integrationLoading,
    error: integrationError,
  } = useNebulaIntegrationsData(repositoryValue);

  // Single source of truth for TDMS services data
  const {
    services: tdmsServices,
    loading: tdmsLoading,
    error: tdmsError,
  } = useTdmsServicesData(repositoryValue);

  const contextValue: RepositoryOnboardingContextValue = useMemo(
    () => ({
      repositoryValue,
      integrationData,
      integrationLoading,
      integrationError,
      tdmsServices,
      tdmsLoading,
      tdmsError,
    }),
    [
      repositoryValue,
      integrationData,
      integrationLoading,
      integrationError,
      tdmsServices,
      tdmsLoading,
      tdmsError,
    ],
  );

  return (
    <RepositoryOnboardingContext.Provider value={contextValue}>
      {children}
    </RepositoryOnboardingContext.Provider>
  );
};

export const useRepositoryOnboardingContext =
  (): RepositoryOnboardingContextValue => {
    const context = useContext(RepositoryOnboardingContext);
    if (context === undefined) {
      throw new Error(
        'useRepositoryOnboardingContext must be used within a RepositoryOnboardingProvider',
      );
    }
    return context;
  };
